import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import { Link } from 'gatsby';

import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import { createStyles, Table as MUITable, withStyles } from '@material-ui/core/';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Pagination from '@material-ui/lab/Pagination';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
  MdRemove as RemoveIcon,
  MdVisibility as VisibilityIcon,
  MdKeyboardArrowRight as KeyboardArrowRightIcon,
  MdKeyboardArrowDown as KeyboardArrowDownIcon,
  MdCheck as CheckIcon,
} from 'react-icons/md';
import { RiEditLine as EditIcon, RiMailSendLine as MailIcon } from 'react-icons/ri';

import { SortIcon, ReviewStatusEnum, getReviewStatusLabel } from '@eisc-frontend/shared-ui';

import { SORT_ORDER, ActionType } from '../constants';

const useStyles = makeStyles((theme) => ({
  header: {
    backgroundColor: theme.palette.common.white,
  },
  relatedTable: {
    border: 'none',
    backgroundColor: '#F5FAfB',
    textAlign: 'left',
  },
  iconButton: {
    border: `1px solid ${theme.palette.text.primary}`,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.common.white,
  },
  iconFix: {
    marginTop: '-8px',
  },
}));

const StyledTableRow = withStyles((theme) =>
  createStyles({
    root: {
      '&:nth-of-type(odd)': {
        backgroundColor: '#F8F4F4',
      },
    },
  }),
)(TableRow);

const boldStatus = [
  ReviewStatusEnum.Submitted,
  ReviewStatusEnum.PreScreening,
  ReviewStatusEnum.DescriptionComplete,
  ReviewStatusEnum.Closed,
];

const Table = (props: any) => {
  const {
    isFetching,
    columns,
    rows,
    rowsPerPage,
    tableLabel,
    onUpdate,
    actions,
    collapsible,
    pagination,
    totalRowsCount,
    filtersChange,
  } = props;
  const classes = useStyles();
  const [openedRow, setOpenedRow] = useState('');
  const [collapseOpen, setCollapseOpen] = useState(false);
  const [order, setOrder] = useState(0);
  const [orderBy, setOrderBy] = useState('');
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
    setOffset(0);
  }, [filtersChange]);

  const sortColumn = async (col) => {
    const newOrder = (order + 1) % SORT_ORDER.length;
    setOrderBy(col);
    setOrder(newOrder);
    onUpdate(offset, col, newOrder);
  };

  const paginateRows = async (page) => {
    const skip = (page - 1) * rowsPerPage;
    setCurrentPage(page);
    setOffset(skip);
    onUpdate(skip, orderBy, order);
  };

  const viewCollapsible = (id) => {
    if (id === openedRow) {
      setCollapseOpen(!collapseOpen);
    } else {
      setOpenedRow(id);
      setCollapseOpen(true);
    }
  };

  const getColSpan = () => {
    return columns.length + actions.length + 1;
  };

  const getRowData = (col, row) => {
    if (row[col.id]?.name) {
      return row[col.id].name;
    }

    return row[col.id];
  };

  const getStatusRow = (col, row) => {
    const rowData = getReviewStatusLabel(getRowData(col, row));

    if (boldStatus.includes(row.status)) {
      return (
        <Tooltip arrow title={rowData}>
          <div className="flex justify-start items-center whitespace-nowrap overflow-hidden overflow-ellipsis">
            <div className="mr-2 h-8 w-8 flex items-center justify-center border border-black rounded bg-white">
              <MailIcon className="h-4 w-4" />
            </div>
            <div className="font-bold">{rowData}</div>
          </div>
        </Tooltip>
      );
    } else {
      return (
        <div className="flex justify-between items-center">
          <Tooltip
            arrow
            title={
              row.status !== ReviewStatusEnum.Draft
                ? `${getReviewStatusLabel(row.status)} and developer emailed`
                : rowData
            }
          >
            <div className="flex justify-start items-center">
              <div className="mr-2 h-8 w-8 flex items-center justify-center">
                <CheckIcon className="text-2xl" />
              </div>
              <div className="whitespace-nowrap overflow-hidden overflow-ellipsis w-30">
                {rowData}
              </div>
            </div>
          </Tooltip>
        </div>
      );
    }
  };

  const getTableRows = (columns, row) => {
    return columns.map((col, index) => {
      const rowData = getRowData(col, row);
      return (
        <TableCell
          key={index}
          align="left"
          className="whitespace-normal overflow-hidden overflow-ellipsis w-30"
        >
          {row[col.id] ? (
            <div className="flex justify-between items-center">
              {col.id === 'status' && getStatusRow(col, row)}
              {col.id === 'title' && (
                <Tooltip arrow title={rowData}>
                  <div className="whitespace-normal overflow-hidden overflow-ellipsis w-30">
                    <Link
                      className="text-blue-900 underline font-semibold"
                      to={`/submission/${row.id}`}
                    >
                      {rowData}
                    </Link>
                  </div>
                </Tooltip>
              )}
              {col.id !== 'status' && col.id !== 'title' && (
                <Tooltip arrow title={rowData}>
                  <div className="whitespace-normal overflow-hidden overflow-ellipsis w-30">
                    {rowData}
                  </div>
                </Tooltip>
              )}
            </div>
          ) : (
            <div
              className={`whitespace-nowrap overflow-hidden overflow-ellipsis w-30 ${
                col.id === 'Phase' && 'font-semibold'
              }`}
            >
              {rowData}
            </div>
          )}
        </TableCell>
      );
    });
  };

  const getTableActions = (row) => {
    return (
      <React.Fragment>
        {actions.map((action, index) => (
          <TableCell className="pr-5" padding="checkbox" align="right" key={index}>
            {!action.isHidden(row) && (
              <Tooltip arrow title={action.label}>
                <IconButton
                  color="secondary"
                  size="small"
                  className={clsx(classes.iconButton, 'w-8 h-8 rounded')}
                  onClick={() => action.onClick(row.id)}
                >
                  {action.label === ActionType.VIEW ? (
                    <VisibilityIcon className="h-4 w-4" />
                  ) : (
                    <EditIcon className="h-4 w-4" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        ))}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      {!isFetching && rows && rows.length > 0 && tableLabel && (
        <Typography variant="subtitle1">
          Showing {rows.length} {tableLabel}
        </Typography>
      )}
      <div className="mt-4 w-auto">
        {isFetching || !rows ? (
          <LinearProgress />
        ) : (
          <TableContainer>
            <MUITable>
              <TableHead>
                <StyledTableRow>
                  <TableCell className="bg-white p-0 m-0 w-0" />
                  {columns.map((col, index) => (
                    <TableCell key={index} className="bg-white pt-3 pb-3">
                      {col.isSortable ? (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={order === 0 ? 'asc' : 'desc'}
                          onClick={() => sortColumn(col.id)}
                        >
                          <div className="flex items-center">
                            <Typography variant="subtitle2" style={{ whiteSpace: 'pre-line' }}>
                              {col.label}
                            </Typography>
                            <div className={classes.iconFix}>
                              <SortIcon />
                            </div>
                          </div>
                        </TableSortLabel>
                      ) : (
                        <Typography variant="subtitle2" style={{ whiteSpace: 'pre-line' }}>
                          {col.label}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  {actions.map((action, index) => (
                    <TableCell
                      key={index}
                      align="right"
                      padding="checkbox"
                      className="bg-white pt-3 pb-3"
                    />
                  ))}
                </StyledTableRow>
              </TableHead>

              <TableBody>
                {rows?.map((row, index) => (
                  <React.Fragment key={index}>
                    <StyledTableRow hover>
                      <TableCell style={{ padding: '5px' }}>
                        {collapsible && row[collapsible]?.length > 0 ? (
                          <div className="m-0 p-0 bg-transparent">
                            <Tooltip arrow title={`${row[collapsible].length} Related`}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => viewCollapsible(row.id)}
                              >
                                {collapseOpen && row.id === openedRow ? (
                                  <KeyboardArrowDownIcon />
                                ) : (
                                  <KeyboardArrowRightIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="m-0 p-0 bg-transparent"></div>
                        )}
                      </TableCell>
                      {getTableRows(columns, row)}
                      {getTableActions(row)}
                    </StyledTableRow>
                    {collapsible && row[collapsible]?.length > 0 && (
                      <TableRow className={classes.relatedTable}>
                        <TableCell colSpan={getColSpan()} className="m-0 p-0 bg-transparent">
                          <Collapse in={row.id === openedRow && collapseOpen}>
                            <MUITable>
                              <TableBody>
                                {row[collapsible].map((relatedRow, index) => (
                                  <TableRow key={index}>
                                    <TableCell padding="checkbox">
                                      <RemoveIcon />
                                    </TableCell>
                                    {getTableRows(columns, relatedRow)}
                                    {getTableActions(relatedRow)}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </MUITable>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
              {pagination && rows?.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={getColSpan()} className="border-0">
                      <div className="flex justify-center my-2">
                        <Pagination
                          count={Math.ceil(totalRowsCount / rowsPerPage)}
                          shape="rounded"
                          color="primary"
                          size="small"
                          disabled={Math.ceil(totalRowsCount / rowsPerPage) <= 1}
                          page={currentPage}
                          onChange={(event, value) => paginateRows(value)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </MUITable>
          </TableContainer>
        )}

        {!isFetching && rows && rows.length === 0 && (
          <div className="p-8">
            <Typography className="text-center" variant="subtitle1">
              No Submissions found
            </Typography>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

Table.propTypes = {
  isFetching: PropTypes.bool,
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array,
  rowsPerPage: PropTypes.number.isRequired,
  tableLabel: PropTypes.string,
  onUpdate: PropTypes.func,
  actions: PropTypes.array,
  collapsible: PropTypes.string,
  pagination: PropTypes.bool,
  totalRowsCount: PropTypes.number.isRequired,
  filtersChange: PropTypes.string,
};

export default Table;
